import { ViteReactSSG } from 'vite-react-ssg';

import { routes } from '@app/router/routes';
import { initSentry } from '@/shared/observability/sentry';
import '@app/providers/i18n'; // funnel i18next bootstrap (side-effect)
import '@app/styles/global.css';
import '@/marketing/index.css'; // Tailwind + design tokens for the marketing site
import { initConsent } from '@shared/consent';
import { initSupportChat } from '@shared/support-chat';

// Before anything else, so a failure during i18n/router setup is still reported.
// No-ops during the prerender pass and whenever VITE_SENTRY_DSN is unset.
initSentry();

/**
 * Stale-deploy recovery (client only). Each deploy replaces the hashed JS chunks
 * (`aws s3 sync --delete` purges the old ones), so a client still running an old
 * index.html can request a chunk that no longer exists. Paired with the
 * CloudFront fix that makes missing `/assets/*` return a real 404, Vite rejects
 * the dynamic import and fires `vite:preloadError`; we do ONE hard reload to pull
 * the fresh index.html + chunk manifest. A 10s cooldown (sessionStorage) breaks
 * reload loops if the reload doesn't resolve it, while still allowing recovery
 * from a later, independent redeploy in the same tab.
 */
if (typeof window !== 'undefined') {
  const LATCH = 'ditChunkReloadAt';
  window.addEventListener('vite:preloadError', (event) => {
    const now = Date.now();
    const last = Number(sessionStorage.getItem(LATCH) || 0);
    if (now - last < 10_000) return; // just reloaded → don't loop
    sessionStorage.setItem(LATCH, String(now));
    event.preventDefault();
    window.location.reload();
  });

  /**
   * Cookie consent (DIT-402) — client only, and deliberately outside the React
   * tree. Every marketing route is prerendered, so a React banner would bake a
   * "not yet asked" state into the static HTML; the library renders its own DOM
   * after load instead. One init covers the marketing site and the funnel both.
   */
  initConsent();

  /**
   * Support chat (DIT-336) — client only, and imperative for the same reason as
   * the banner above: prerendered routes have nothing to hydrate it against.
   * Anonymous by definition here; it no-ops where no inbox is configured.
   */
  void initSupportChat();
}

/**
 * Entry — vite-react-ssg (DIT-264 / S7). `vite-react-ssg build` prerenders the
 * marketing/blog routes to static HTML (the `includedRoutes` list in
 * vite.config.ts), while the signup funnel stays CSR (no HTML emitted, served by
 * the SPA shell). A plain `vite build` / `vite dev` uses the same entry as a
 * client SPA. Providers (MUI theme, emotion) live in the `RootProviders` route.
 */
export const createRoot = ViteReactSSG({ routes });
